import Hls from "hls.js";

export interface VideoPlayerEvents {
  onPlay?: () => void;
  onPause?: () => void;
  onError?: (error: any) => void;
  onLoadedData?: (duration: number) => void;
  onEnded?: () => void;
  onWaiting?: () => void;
  onCanPlay?: () => void;
  onTimeUpdate?: (currentTime: number) => void;
  onSeeked?: () => void;
}

export class VideoPlayerIns {
  private video: HTMLVideoElement;
  private hls?: Hls;
  private events: VideoPlayerEvents;
  public totalDuration: number = 0;

  constructor(video: HTMLVideoElement, events: VideoPlayerEvents = {}) {
    this.video = video;
    this.events = events;
    this.initializeEventListeners();
  }

  public initializeEventListeners() {
    const handlePlay = () => {
      console.log("play");
      this.events.onPlay?.();
    };

    const handlePause = () => {
      console.log("pause");
      this.events.onPause?.();
    };

    const handleError = (e: any) => {
      console.log("error", e);
      this.events.onError?.(e);
    };

    const handleLoadedData = () => {
      console.log("loadeddata");
      this.totalDuration = this.video.duration;
      this.events.onLoadedData?.(this.totalDuration);
    };

    const handleEnded = (e: any) => {
      console.log("ended", e);
      this.events.onEnded?.();
    };

    const handleWaiting = (e: any) => {
      console.log("waiting", e);
      this.events.onWaiting?.();
    };

    const handleCanPlay = (e: any) => {
      console.log("canplay", e);
      this.events.onCanPlay?.();
    };

    const handleTimeUpdate = () => {
      const currentTime = this.video.currentTime;
      //   console.log('timeupdate:', currentTime);
      this.events.onTimeUpdate?.(currentTime);
    };


    const handleSeeked = () => {
      //   console.log('seeking end, current time:', this.video.currentTime);
      this.events.onSeeked?.();
    };

    this.video.addEventListener("play", handlePlay);
    this.video.addEventListener("pause", handlePause);
    this.video.addEventListener("error", handleError);
    this.video.addEventListener("loadedmetadata", handleLoadedData);
    this.video.addEventListener("ended", handleEnded);
    this.video.addEventListener("waiting", handleWaiting);
    this.video.addEventListener("canplay", handleCanPlay);
    this.video.addEventListener("timeupdate", handleTimeUpdate);
    this.video.addEventListener('seeked', handleSeeked);

    return () => {
      this.video.removeEventListener("play", handlePlay);
      this.video.removeEventListener("pause", handlePause);
      this.video.removeEventListener("error", handleError);
      this.video.removeEventListener("loadedmetadata", handleLoadedData);
      this.video.removeEventListener("ended", handleEnded);
      this.video.removeEventListener("waiting", handleWaiting);
      this.video.removeEventListener("canplay", handleCanPlay);
      this.video.removeEventListener("timeupdate", handleTimeUpdate);
      this.video.removeEventListener('seeked', handleSeeked);
    };
  }

  public loadVideo(videoUrl: string): void {
    if (Hls.isSupported()) {
      this.hls = new Hls({ enableWorker: true, lowLatencyMode: true, backBufferLength: 90 });

      // 设置 HLS 错误处理
      this.hls.on(Hls.Events.ERROR, (_, data) => {
        console.log('HLS Error:', data);

        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log('致命的媒体错误，尝试恢复');
              this.hls?.recoverMediaError();
              break;
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.log('致命的网络错误，尝试重新开始加载');
              this.hls?.startLoad();
              break;
            default:
              console.log('无法恢复的错误，销毁播放器');
              this.events.onError?.(data);
              this.hls?.destroy();
              break;
          }
        }
      });

      this.hls.loadSource(videoUrl);
      this.hls.attachMedia(this.video);
      console.log("hls loaded");
    } else if (this.video.canPlayType("application/vnd.apple.mpegurl")) {
      this.video.src = videoUrl;
      this.events.onLoadedData?.(this.video.duration);
      console.log("video origin loaded");
    } else {
      console.error("This browser does not support HLS.");
    }
  }

  public play(): void {
    console.log("click play");
    this.video.play();
  }

  public toggle(): void {
    const isPlayingFlag = this.isPlaying();
    if (isPlayingFlag) {
      this.pause();
    } else {
      this.play();
    }
  }

  public pause(): void {
    console.log("click pause");
    this.video.pause();
  }

  public setCurrentTime(seconds: number): void {
    console.log("set current time:", seconds);
    this.video.currentTime = seconds;
    this.events.onTimeUpdate?.(seconds);
  }

  public setPlaybackRate(rate: number): void {
    console.log("set playback rate:", rate);
    this.video.playbackRate = rate;
  }

  public destroy(): void {
    // 移除 HLS 事件监听器
    this.hls?.off(Hls.Events.ERROR);
    this.hls?.destroy();
  }

  // 获取当前播放时间（秒）
  public getCurrentTime(): number {
    return this.video.currentTime;
  }

  // 获取视频总时长（秒）
  public getDuration(): number {
    return this.video.duration;
  }

  // 获取是否正在播放
  public isPlaying(): boolean {
    return !this.video.paused;
  }
}
